import { useIsFetching, type Query } from '@tanstack/react-query';
import { useSyncExternalStore } from 'react';

/**
 * Whether a query in flight should raise the route bar.
 *
 * The bar says "the page you asked for is on its way", so a **poll** never
 * raises it: the fleet map asks every 10 s and the bell every minute, and a
 * bar that flickers on a timer stops meaning anything. A query is a poll when
 * any of its observers asked for a `refetchInterval`. Everything else that is
 * fetching — a first load, a page change, a refetch after a save — counts.
 */
export function raisesRouteBar(query: Query): boolean {
  if (query.state.fetchStatus !== 'fetching') return false;
  return !query.observers.some((observer) => Boolean(observer.options.refetchInterval));
}

/*
 * Lazy routes: the Suspense fallback mounts `RouteBarSignal`, which holds the
 * bar up for as long as it is mounted. A counter rather than a flag, because
 * two boundaries can be waiting at once.
 */
let pending = 0;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((listener) => listener());

export const routeBarSignal = {
  hold(): () => void {
    pending += 1;
    emit();
    return () => {
      pending -= 1;
      emit();
    };
  },
  subscribe(listener: () => void) {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },
  get: () => pending > 0,
};

export function useRouteBarActive(): boolean {
  const fetching = useIsFetching({ predicate: raisesRouteBar });
  const suspended = useSyncExternalStore(routeBarSignal.subscribe, routeBarSignal.get, routeBarSignal.get);
  return fetching > 0 || suspended;
}
